import type { ApplicationAccessList } from 'src/api/main'

/**
 * `applicationsAccessCreate` is declarative — it sets a member's COMPLETE app
 * list, so every other app they can reach must ride along in the payload or
 * it gets revoked. Builds that array with this app's entry replaced by
 * `appEntry`, or removed entirely when `appEntry` is null.
 */
export const buildAppAccessApplications = (
  existingAccess: readonly ApplicationAccessList[],
  applicationId: number,
  appEntry: { role: string | null } | null
): { id: number; role: string | null }[] => [
  ...existingAccess
    .filter((a) => a.application.id !== applicationId)
    .map((a) => ({ id: a.application.id, role: a.roles[0]?.name ?? null })),
  ...(appEntry ? [{ id: applicationId, role: appEntry.role }] : [])
]

/** Ids of the roles an application owns, from its `related_roles`. */
export const appRoleIdSet = (
  relatedRoles: readonly { id: number }[] | undefined | null
): Set<number> => new Set((relatedRoles ?? []).map((role) => role.id))

/**
 * Narrows a member's roles to the DISTINCT roles this application owns.
 *
 * Two separate causes produced the "wall of Admin tags" bug, and this guards
 * both:
 *
 * 1. `ApplicationAccessList.roles` is not scoped to the app on the row — it
 *    carries the member's app-scoped roles across the whole workspace. Every
 *    app install mints its own "Admin" role, so an admin of eight apps holds
 *    eight distinct roles all named "Admin".
 * 2. The same role id can repeat within that array (one entry per underlying
 *    grant), and a repeated role is never meaningful to show twice.
 *
 * Deduping by id rather than by name is deliberate: the names collide across
 * apps, so name-keyed dedupe would collapse genuinely different roles.
 */
export const rolesOwnedByApp = <T extends { id: number }>(
  memberRoles: readonly T[] | undefined | null,
  appRoleIds: ReadonlySet<number>
): T[] => {
  const seen = new Set<number>()

  return (memberRoles ?? []).filter((role) => {
    if (!appRoleIds.has(role.id) || seen.has(role.id)) {
      return false
    }
    seen.add(role.id)

    return true
  })
}

/**
 * A member holds at most one role per application, so a seeded or submitted
 * selection is clamped to a single id. Guards against stale multi-role data
 * from before that rule was enforced silently re-saving itself.
 */
export const clampToSingleRole = (roleIds: readonly number[]): number[] =>
  roleIds.length > 1 ? [roleIds[0]!] : [...roleIds]
